/**
 * @broadpaper/ingest — an uploaded or pasted file becomes a BroadPaper data
 * source, with types nobody had to declare.
 *
 * Private to BroadPaper Cloud and never published. It is a package rather than
 * a folder inside the application for two reasons: it is the piece most worth
 * unit-testing on its own, and it has to run in both places — in the browser, so
 * that a pasted table is understood without a round trip and an uploaded
 * workbook is never sent anywhere, and on the server, where a dataset is
 * re-checked before it is stored.
 *
 * The whole contract is {@link ingest}: bytes or text in, one or more
 * {@link Dataset} out, each with columns whose types were decided by a vote and
 * whose rows are already coerced to them. {@link dataSourceFor} and
 * {@link reportDataFor} turn one of those into the `DataSource` and `ReportData`
 * the rest of BroadPaper already speaks — there is no Cloud-only data model.
 */
import { parseDelimited } from "./delimited.js";
import { JsonShapeError, parseJsonText } from "./json.js";
import { labelFromFilename, sniffBytes, sniffText } from "./sniff.js";
import { tableToDataset, type Cell } from "./table.js";
import { readWorkbook, XlsxError } from "./xlsx.js";
import { DEFAULT_MAX_COLUMNS, DEFAULT_MAX_ROWS, type Dataset, type IngestOptions, type IngestResult, type Notice } from "./types.js";

export type { Dataset, IngestOptions, IngestResult, Notice, SourceColumn, SourceFormat } from "./types.js";
export { DEFAULT_MAX_COLUMNS, DEFAULT_MAX_ROWS, INFERENCE_SAMPLE } from "./types.js";
export { DATASET_SOURCE_ID, dataSourceFor, fieldSchema, previewValue, reportDataFor } from "./source.js";
export { plain, recoerce, tableToDataset } from "./table.js";
export type { Cell, TableOptions } from "./table.js";
export { blank, booleanWord, coerce, inferColumn, looksLikeHeaderRow, parseDate, parseNumeric, writtenDateFormat } from "./infer.js";
export { entryText, holds, newColumn, readEntry, retype, summarise } from "./edit.js";
export { refreshDataset } from "./refresh.js";
export type { Refreshed } from "./refresh.js";
export type { Entry } from "./edit.js";
export type { ColumnVote } from "./infer.js";
export { identifier, tidyLabel, unique } from "./names.js";
export { labelFromFilename, sniffBytes, sniffText } from "./sniff.js";
export { parseDelimited, sniffDelimiter } from "./delimited.js";
export { readWorkbook, XlsxError, XLSX_LIMITS, columnIndex, serialToDate } from "./xlsx.js";
export type { Sheet, XlsxLimits } from "./xlsx.js";
export { JsonShapeError } from "./json.js";

/**
 * The error a user is shown.
 *
 * Every failure path in this package ends here, with a sentence written for the
 * person who chose the file and — where there is one — a hint at what to do
 * instead. "Invalid JSON structure" is not an error message; "We couldn't read
 * this file as JSON. Check that it contains a valid object, or a list of
 * records." is.
 */
export class IngestError extends Error {
  constructor(
    message: string,
    /** What the user could do about it, when there is something. */
    readonly hint?: string
  ) {
    super(message);
    this.name = "IngestError";
  }
}

/** Reads a file the user chose. The bytes are never uploaded to read them. */
export async function ingestFile(file: File, opts: IngestOptions = {}): Promise<IngestResult> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  return ingest(bytes, { filename: file.name, ...opts });
}

/** Reads pasted text. Excel and Google Sheets both put TSV on the clipboard. */
export function ingestText(text: string, opts: IngestOptions = {}): IngestResult {
  return run(sniffText(text, opts.filename), text, null, opts);
}

export function ingest(input: Uint8Array | string, opts: IngestOptions = {}): IngestResult {
  if (typeof input === "string") return ingestText(input, opts);
  return run(sniffBytes(input, opts.filename), null, input, opts);
}

function run(format: ReturnType<typeof sniffText>, text: string | null, bytes: Uint8Array | null, opts: IngestOptions): IngestResult {
  const maxRows = opts.maxRows ?? DEFAULT_MAX_ROWS;
  const maxColumns = opts.maxColumns ?? DEFAULT_MAX_COLUMNS;
  const shared = { maxColumns, dayFirst: opts.dayFirst !== false };
  // One row more than the plan holds, because the readers stop before the heading
  // row is decided and would otherwise spend a row of the plan on it. The cap on
  // data rows is applied by `tableToDataset`, which knows.
  const readRows = maxRows + 1;

  switch (format) {
    case "unknown":
      // Reached for the old binary Excel format, which `sniffBytes` recognises
      // by its OLE signature specifically so this can say what to do about it.
      // Falling through to the text reader instead would produce one nonsense
      // column of mojibake and a user who thinks their file is corrupt.
      throw new IngestError(
        "We couldn't read this file.",
        "If it is an older Excel workbook, open it and save it again as .xlsx. A CSV export works too."
      );

    case "xlsx": {
      if (!bytes) throw new IngestError("We couldn't read this file.");
      let sheets;
      try {
        sheets = readWorkbook(bytes, { maxRows: readRows, maxColumns });
      } catch (e) {
        if (e instanceof XlsxError) throw new IngestError(e.message);
        throw new IngestError("We couldn't read this file as a spreadsheet.", "If it came from another program, try exporting it as CSV.");
      }
      // A sheet with nothing in it is not a dataset. Workbooks routinely carry
      // two or three of those, and offering the user "Sheet3 (empty)" as a
      // choice is noise rather than information.
      const datasets = sheets
        .filter((s) => s.rows.length > 0)
        .map((s) => tableToDataset(s.rows as Cell[][], { label: s.name, truncated: s.truncated, maxRows, ...shared }));
      if (!datasets.length) {
        throw new IngestError("This workbook has no data in it.", "Check that the rows you want are in the workbook you uploaded, and not in a linked file.");
      }
      const notices: Notice[] = [];
      if (sheets.length > datasets.length) {
        notices.push({ level: "info", message: `${sheets.length - datasets.length} empty sheet${sheets.length - datasets.length === 1 ? "" : "s"} in this workbook ${sheets.length - datasets.length === 1 ? "was" : "were"} skipped.` });
      }
      return { format, datasets, notices };
    }

    case "json":
    case "ndjson": {
      const body = text ?? new TextDecoder().decode(bytes!);
      let table;
      try {
        table = parseJsonText(body, { maxRows });
      } catch (e) {
        if (e instanceof JsonShapeError) throw new IngestError(e.message);
        throw new IngestError("We couldn't read this file as JSON.", "Check that it contains a valid object, or a list of records.");
      }
      const grid: Cell[][] = [table.headers, ...table.rows];
      const dataset = tableToDataset(grid, {
        label: labelFromFilename(opts.filename, table.singleObject ? "Record" : "Imported data"),
        // JSON headings are keys, not a row of the data — saying so avoids the
        // header-row heuristic being asked a question it cannot get right on a
        // one-record file.
        headerRow: true,
        ...shared
      });
      return { format, datasets: [dataset], notices: table.notices };
    }

    default: {
      const body = text ?? new TextDecoder().decode(bytes!);
      if (!body.trim()) throw new IngestError("There was nothing in this file.");
      const parsed = parseDelimited(body, { maxRows: readRows });
      if (!parsed.rows.length) throw new IngestError("There was nothing in this file.");
      const dataset = tableToDataset(parsed.rows as Cell[][], {
        label: labelFromFilename(opts.filename, "Pasted data"),
        truncated: parsed.truncated,
        maxRows,
        ...shared
      });
      return { format: parsed.delimiter === "\t" ? "tsv" : "csv", datasets: [dataset], notices: [] };
    }
  }
}

/**
 * The questions a review screen should actually put to the user.
 *
 * Everything else a dataset has to say is either settled or informational, and
 * putting a settled decision in front of somebody as a question is how a
 * two-second upload becomes a five-step wizard. This is the filter that keeps
 * the review screen to the things where the user's judgement is genuinely
 * needed.
 */
export function questionsFor(dataset: Dataset): Notice[] {
  return dataset.notices.filter((n) => n.level === "ask");
}

/** Everything worth showing but not worth asking about. */
export function remarksFor(dataset: Dataset): Notice[] {
  return dataset.notices.filter((n) => n.level !== "ask");
}
